/**
 * Normalize unknown thrown values (especially Supabase PostgREST objects) into Error instances.
 * Prevents Sentry noise like `Error: [object Object]` when rejections are plain objects.
 */

export type NormalizedError = Error & {
  code?: string;
  details?: string;
  hint?: string;
  cause?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readStringField(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Convert any thrown/rejected value into an Error with a readable message.
 * PostgREST-like objects (`{ message, code, details, hint }`) keep structured fields
 * attached for Sentry extras.
 */
export function normalizeToError(value: unknown): NormalizedError {
  if (value instanceof Error) {
    return value as NormalizedError;
  }

  if (isRecord(value)) {
    const message =
      readStringField(value, "message") ?? readStringField(value, "error") ?? "Unknown error";
    const err = new Error(message) as NormalizedError;
    err.cause = value;
    const code = readStringField(value, "code");
    const details = readStringField(value, "details");
    const hint = readStringField(value, "hint");
    if (code !== undefined) err.code = code;
    if (details !== undefined) err.details = details;
    if (hint !== undefined) err.hint = hint;
    return err;
  }

  if (value === null || value === undefined) {
    return new Error(String(value));
  }

  return new Error(String(value));
}

/**
 * Extract PostgREST-style fields from a normalized error for Sentry context.
 */
export function getErrorTrackingExtras(error: Error): Record<string, unknown> | undefined {
  const normalized = error as NormalizedError;
  const extras: Record<string, unknown> = {};
  if (normalized.code !== undefined) extras.code = normalized.code;
  if (normalized.details !== undefined) extras.details = normalized.details;
  if (normalized.hint !== undefined) extras.hint = normalized.hint;
  return Object.keys(extras).length > 0 ? extras : undefined;
}
